/**
 * PayPal Agentic Commerce Service
 *
 * Functions for connecting to PayPal Agentic Commerce.
 */

import Config from '../../config';
import axios from 'axios';

/**
 * Connection status enum
 */
export const ConnectionStatus = {
  CONNECTED: 'connected',
  NOT_CONNECTED: 'not_connected',
  PENDING: 'pending',
  ERROR: 'error',
} as const;

export type ConnectionStatusType = (typeof ConnectionStatus)[keyof typeof ConnectionStatus];

/**
 * A connector action defined by the backend
 */
export interface ConnectorAction {
  Name: string;
  FriendlyName: string;
}

/**
 * Return type for getPayPalConnectionStatus
 */
export interface PayPalConnectionResult {
  status: ConnectionStatusType;
  settings: Record<string, string | null>;
  actions: ConnectorAction[];
  isConnected: boolean;
}

/**
 * Shape of a single setting from the backend GetAllSystemSettings response
 */
interface SystemSetting {
  Name: string;
  Value: string | null;
}

/**
 * Shape of a single system from the backend GetAllSystemSettings response
 */
interface SystemSettingsEntry {
  System: string;
  IsConnected: boolean;
  Settings: SystemSetting[];
  Actions?: ConnectorAction[];
}

/**
 * Get PayPal system settings and connection status from the connector settings API
 */
export const getPayPalConnectionStatus = async (): Promise<PayPalConnectionResult> => {
  try {
    const res = await axios.get<{ value: SystemSettingsEntry[] }>(
      Config.api + '/odata/Company/Functions.GetAllSystemSettings'
    );
    const paypal = res.data.value.find((r) => r.System === 'PayPal');

    if (!paypal) {
      return {
        status: ConnectionStatus.NOT_CONNECTED,
        settings: {},
        actions: [],
        isConnected: false,
      };
    }

    // Convert settings array to object
    const settingsObj = (paypal.Settings || []).reduce<Record<string, string | null>>((acc, setting) => {
      acc[setting.Name] = setting.Value;
      return acc;
    }, {});

    return {
      status: paypal.IsConnected ? ConnectionStatus.CONNECTED : ConnectionStatus.NOT_CONNECTED,
      settings: settingsObj,
      actions: paypal.Actions || [],
      isConnected: paypal.IsConnected || false,
    };
  } catch (error) {
    console.error('Error fetching PayPal connection status:', error);
    return {
      status: ConnectionStatus.ERROR,
      settings: {},
      actions: [],
      isConnected: false,
    };
  }
};

/**
 * Execute the "Onboard PayPal Agentic" connector action
 * @param settings - The PayPal settings from the form
 * @returns The result message or redirect URL
 */
export const initiatePayPalOnboarding = async (settings: Record<string, unknown>): Promise<string> => {
  const result = await axios.post<string>(
    Config.api + `/odata/Company/Functions.ExecuteConnectorAction`,
    settings,
    { params: { system: 'PayPal', actionName: 'OnboardPayPalAgentic' } }
  );
  return result.data;
};

/**
 * Save PayPal system settings
 * @param settings - The PayPal settings to save
 */
export const savePayPalSettings = async (settings: Record<string, unknown>): Promise<void> => {
  const payload: Record<string, unknown> = { ...settings, System: 'PayPal' };
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined) {
      payload[k] = null;
    }
  });
  await axios.put(Config.api + `/odata/Company/Functions.SetSystemSettings`, payload, { params: { system: 'PayPal' } });
};

/**
 * Disconnect PayPal account from Logicbroker
 */
export const disconnectPayPal = async (): Promise<void> => {
  await axios.get(Config.api + `/odata/Company/Functions.DisconnectOAuth?system=PayPal`);
};
